import { defineComponent as _defineComponent } from 'vue'
import { defineMessages } from '@/lib/i18n';
import { FormItem } from '@/interfaces/lib/model';
import { PropType, computed, ref, onMounted, watch } from 'vue';
import { CreatedUser } from '@/interfaces/api/general/user';
import { getFullName } from '@/utils/string';
import { formatToDate, isWithinHours } from '@/utils/datetime';
import router from '@/router';
import CheckListFilterPanel from '@/views/pages/dynamicList/CheckListFilterPanel.vue';
import SdsTablePseudoScrollBar from '@/views/dashboard/SdsTablePseudoScrollBar.vue';
import { SortState } from '@/composables/useSds';

import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { store } from '@/store/index';
import { readIsFeatureEnabled } from '@/store/main/getters';

export interface DynamicList {
  name: string;
  description: string;
  created_by: string;
  created_user?: CreatedUser;
  total: number;
  updated_at: string;
  suspended_at: string | null;
  is_query_ready: boolean;
  id: number;
  created_at: string;
  version_updated_at: string;
  law_names?: string[];
  regulation_names?: string[];
  is_new?: boolean;
  is_favorite?: boolean;
  new_sds_count?: number;
  dynamic_list_template_id?: number | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicListTable',
  props: {
  dynamicLists: {
    type: Array as PropType<Array<DynamicList>>,
    default: () => [],
  },
  total: {
    type: Number,
    default: 1,
  },
  page: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 50,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  selectedDynamicLists: {
    type: Array as PropType<DynamicList[]>,
    default: () => [],
  },
  isFilterPanelVisible: {
    type: Boolean,
    required: true,
  },
  sortState: {
    type: Object as PropType<SortState>,
    default: () => ({
      updated_at: 'none',
      created_at: 'none',
    }),
  },
  toggleSort: {
    type: Function as PropType<(column: string) => void>,
    required: true,
  },
},
  emits: ["update:page", "update:itemsPerPage", "fetchItems", "updateSelectedDynamicLists", "cancelFilters", "update:isFilterPanelVisible"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:page', page: number): void;
  (e: 'update:itemsPerPage', itemsPerPage: number): void;
  (e: 'fetchItems'): void;
  (e: 'updateSelectedDynamicLists', lists: DynamicList[]): void;
  (e: 'cancelFilters'): void;
  (e: 'update:isFilterPanelVisible', isVisible: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  newLabel: 'NEW',
  extractionConditionUpdateDate: '抽出条件更新日',
  subjectToRenewal: '条件更新あり',
});



const totalLength = ref<number>(props.total);



const filteredDynamicLists = ref<DynamicList[]>([...props.dynamicLists]);
const updateFilteredDynamicLists = (dynamicLists) => {
  filteredDynamicLists.value = dynamicLists;
  isFilterPanelVisible.value = false;
  emits('update:isFilterPanelVisible', false);
  totalLength.value = dynamicLists.value.length;
};

const isChecklistLawRegulationEnabled = computed(() => {
  return (
    readIsFeatureEnabled(store)('use_new_regulatory_screening_ui') ?? false
  );
});

const isBeforeStart = (item: DynamicList): boolean => {
  if (!item.is_query_ready) return true;
  if (
    isChecklistLawRegulationEnabled.value &&
    item.total === 0 &&
    isWithinHours(item.created_at, 24)
  ) {
    return true;
  }
  return false;
};

watch(
  () => props.isFilterPanelVisible,
  (newVal) => {
    isFilterPanelVisible.value = newVal;
  }
);

const isFilterPanelVisible = ref(props.isFilterPanelVisible);
const handleCancelFilters = (isVisible: boolean) => {
  isFilterPanelVisible.value = isVisible;
  emits('update:isFilterPanelVisible', isVisible);
};

const isAllChecked = computed(() => {
  if (
    !props.dynamicLists ||
    props.dynamicLists.length === 0 ||
    !props.selectedDynamicLists
  ) {
    return false;
  }
  return (
    props.selectedDynamicLists.length ===
    props.dynamicLists.filter((item) => item.is_query_ready).length
  );
});

const isIndeterminate = computed(() => {
  if (!props.dynamicLists || !props.selectedDynamicLists) {
    return false;
  }
  return (
    props.selectedDynamicLists.length > 0 &&
    props.selectedDynamicLists.length <
      props.dynamicLists.filter((item) => item.is_query_ready).length
  );
});

const handleClickAllCheck = (checked: boolean) => {
  if (checked) {
    emits(
      'updateSelectedDynamicLists',
      props.dynamicLists.filter((item) => item.is_query_ready)
    );
    return;
  }
  emits('updateSelectedDynamicLists', []);
};

const handleCheckDynamicLists = (item: DynamicList, checked: boolean) => {
  const newValue = checked
    ? [...props.selectedDynamicLists, item]
    : props.selectedDynamicLists.filter((list) => list.id !== item.id);
  emits('updateSelectedDynamicLists', newValue);
};

const updatePage = (e: number) => {
  emits('update:page', e);
};

const updateItemsPerPage = (e: number) => {
  emits('update:itemsPerPage', e);
};

const fetchItems = () => {
  emits('fetchItems');
};

const headers = computed(() => {
  const baseHeaders = [
    { text: '', value: 'check', sortable: false, width: '30px' },
    {
      text: $t(sharedMessages.listName),
      sortable: false,
      value: 'name',
      width: '200px',
    },
    {
      text: $t(sharedMessages.targetSDS),
      value: 'total',
      sortable: false,
      width: '65px',
    },
  ];

  const lawRegulationHeaders = [
    {
      text: $t(sharedMessages.targetLaws),
      value: 'law_names',
      sortable: false,
      width: '200px',
    },
    {
      text: $t(sharedMessages.targetRegulations),
      value: 'regulation_names',
      sortable: false,
      width: '200px',
    },
  ];

  const summaryHeader = [
    {
      text: $t(sharedMessages.summary),
      value: 'description',
      sortable: false,
      width: '400px',
    },
  ];

  const trailingHeaders = [
    {
      text: $t(m.extractionConditionUpdateDate),
      value: 'version_updated_at',
      sortable: false,
      width: '120px',
    },
    {
      text: $t(sharedMessages.lastUpdatedDate),
      value: 'updated_at',
      sortable: false,
      width: '100px',
    },
    {
      text: $t(sharedMessages.creationDate),
      value: 'created_at',
      sortable: false,
      width: '100px',
    },
    {
      text: $t(sharedMessages.creator),
      value: 'created_user',
      sortable: false,
      width: '90px',
    },
  ];

  return [
    ...baseHeaders,
    ...(isChecklistLawRegulationEnabled.value
      ? lawRegulationHeaders
      : summaryHeader),
    ...trailingHeaders,
  ];
});

const footerProps = computed(() => ({
  'items-per-page-options': [50, 100, 200],
  'items-per-page-text': $t(sharedMessages.itemsPerPageText),
}));

const navigate = (id) => {
  router.push({
    name: 'sdsDynamicListResult',
    params: { id: id },
  });
};

const pageSelectItems = computed((): Array<FormItem> => {
  return [
    ...Array(Math.ceil(totalLength.value / props.itemsPerPage)).keys(),
  ].map((i) => {
    return {
      value: i + 1,
      text: `${i + 1} / ${Math.ceil(
        totalLength.value / props.itemsPerPage
      )} (${$t(sharedMessages.totalItems, {
        total: totalLength.value.toLocaleString(),
      })})`,
    };
  });
});

const isWithinTwoWeeks = (date: string) => {
  const currentDate = new Date();
  const jstOffset = 9 * 60; // 日本はUTC+9時間
  currentDate.setMinutes(
    currentDate.getMinutes() + currentDate.getTimezoneOffset() + jstOffset
  );

  const targetDate = new Date(date);
  targetDate.setMinutes(
    targetDate.getMinutes() + targetDate.getTimezoneOffset() + jstOffset
  );

  // 2週間（14日）以内かどうかを比較
  const timeDifference = currentDate.getTime() - targetDate.getTime();
  const twoWeeksInMs = 14 * 24 * 60 * 60 * 1000;

  return timeDifference <= twoWeeksInMs && timeDifference >= 0;
};

watch(
  () => props.dynamicLists,
  (newValue) => {
    if (newValue && newValue.length > 0) {
      filteredDynamicLists.value = [...newValue];
    }
  },
  { immediate: true }
);
onMounted(() => {
  if (props.dynamicLists && props.dynamicLists.length > 0) {
    filteredDynamicLists.value = [...props.dynamicLists];
  }
});

return { __sfc: true,$t, m, props, totalLength, emits, filteredDynamicLists, updateFilteredDynamicLists, isChecklistLawRegulationEnabled, isBeforeStart, isFilterPanelVisible, handleCancelFilters, isAllChecked, isIndeterminate, handleClickAllCheck, handleCheckDynamicLists, updatePage, updateItemsPerPage, fetchItems, headers, footerProps, navigate, pageSelectItems, isWithinTwoWeeks, getFullName, formatToDate, CheckListFilterPanel, SdsTablePseudoScrollBar, sharedMessages }
}

})